/**
 * Resolving an allowance for a take-off row, and the job's grade tier.
 *
 * The table and where its numbers came from: `productAllowances.js`.
 *
 * An allowance is the LAST rung of the price ladder, under the catalog — a
 * price anybody actually has always wins (the user's decision, 2026-09-22:
 * "if we bid a particular product, then that price should be shown").
 *
 * Pure. No React, no Firestore, no environment.
 */

const {
  PRODUCT_ALLOWANCES,
  ALLOWANCE_ALIASES,
  NO_ALLOWANCE,
} = require('./productAllowances');

/** The three tiers, low to high. A bid quotes `typical` and ranges over all three. */
const TIERS = ['low', 'typical', 'high'];

/**
 * The job's grade, chosen by the contractor. The names are the cabinet
 * families' (`shared-lib/cabinet-runs/families.js`), because that is how a
 * contractor already thinks about grade on our own screens and it is the one
 * grade choice the app already persists.
 */
// The keys are the cabinet families' grades, stored on the structure
// (`allowanceGrade`) and the estimate; the labels are what a contractor reads
// (user, 2026-10-14) — "Stock" beside a vendor's cabinet line read as a
// cabinet choice, which this is not.
const ALLOWANCE_TIERS = {
  stock: { key: 'stock', label: 'Standard', tier: 'low' },
  'semi-custom': { key: 'semi-custom', label: 'Premium', tier: 'typical' },
  custom: { key: 'custom', label: 'Luxury', tier: 'high' },
};

/**
 * Semi-custom, matching `DEFAULT_FAMILY_ID` — the family an unset cabinet run
 * already solves as, so an estimate with no grade chosen prices the cabinets
 * it is already drawing.
 */
const DEFAULT_ALLOWANCE_GRADE = 'semi-custom';

/** The grade for a stored value, falling back to the default. */
const resolveAllowanceGrade = (value) =>
  ALLOWANCE_TIERS[value] ?? ALLOWANCE_TIERS[DEFAULT_ALLOWANCE_GRADE];

/** A cabinet family id (`generic-stock`, …) as a grade key, or null. */
const gradeFromCabinetFamily = (familyId) => {
  if (!familyId) return null;
  const id = String(familyId);
  if (id.includes('stock')) return 'stock';
  if (id.includes('semi-custom')) return 'semi-custom';
  if (id.includes('custom')) return 'custom';
  return null;
};

/**
 * What a cabinet's built-in seed is multiplied by at each grade.
 *
 * NCE's own adjustments off its standard-grade cabinet prices (p.29): "Add 65%
 * to material costs for premium grade cabinets … Deduct 45% … for economy
 * grade". Our per-box seeds are fitted to NCE's standard grade
 * (`CABINET_PRICE_RULES`, 2026-10-15), so standard is 1.0 and the other two
 * are NCE's numbers.
 *
 * This is why a cabinet has no allowance-table entry: it is never unpriced,
 * it is priced at one grade and needs scaling to the others.
 */
const CABINET_GRADE_MULTIPLIER = {
  stock: 0.55,
  'semi-custom': 1.0,
  custom: 1.65,
};

const CABINET_SURFACE = 'cabinet';

/** The allowance-table key for a row, or null when it has no entry. */
const allowanceKeyFor = (row) => {
  if (!row) return null;
  // A row that IS an allowance names its entry (the floor a re-laid-out room
  // gets before one is chosen, `addFloorAllowance`).
  if (row.allowanceKey && PRODUCT_ALLOWANCES[row.allowanceKey]) {
    return row.allowanceKey;
  }
  // An area row is keyed by the SURFACE it covers where the surface decides
  // the product (a countertop is stone whatever the texture is called), and
  // otherwise by its material type (tile).
  if (!row.objectType) {
    if (row.surfaceType && PRODUCT_ALLOWANCES[row.surfaceType]) {
      return row.surfaceType;
    }
    return row.type && PRODUCT_ALLOWANCES[row.type] ? row.type : null;
  }
  const type = row.objectType;
  if (!type || NO_ALLOWANCE.has(type)) return null;
  // One type, two products: a recessed can and a pendant are both a
  // `ceiling-light`, and they are an order of magnitude apart.
  const withVariant = row.variant ? `${type}:${row.variant}` : null;
  if (withVariant && PRODUCT_ALLOWANCES[withVariant]) return withVariant;
  if (withVariant && ALLOWANCE_ALIASES[withVariant]) {
    return ALLOWANCE_ALIASES[withVariant];
  }
  if (PRODUCT_ALLOWANCES[type]) return type;
  const alias = ALLOWANCE_ALIASES[type];
  return alias && PRODUCT_ALLOWANCES[alias] ? alias : null;
};

/**
 * The allowance for a row at a grade, as the price ladder's bottom rung.
 *
 * Returns `{ cost, low, high, unit, key, source, derived }` — `cost` is the
 * grade's own figure and `low` / `high` are the ends of the range the bid
 * shows — or null where the row has no entry, which leaves it unpriced
 * exactly as before.
 *
 * A per-ft² entry is returned per unit of area; the caller multiplies by the
 * row's area the same way it does a catalog price.
 */
const allowanceFor = (row, grade) => {
  if (row?.allowanceParts) return blendedAllowance(row.allowanceParts, grade);
  const key = allowanceKeyFor(row);
  if (!key) return null;
  const entry = PRODUCT_ALLOWANCES[key];
  const tier = resolveAllowanceGrade(grade).tier;
  return {
    key,
    cost: entry[tier],
    low: entry.low,
    high: entry.high,
    unit: entry.unit,
    source: entry.source,
    derived: !!entry.derived,
  };
};

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * A row whose units carry different entries — `allowanceParts`, `[{ key,
 * count }]`: two showers on one row, one whose glass a bundle supplies (the
 * pan alone) and one whose it does not (the whole insert,
 * `showerGlassSupply.js`) — at the per-unit figure that bills each its own:
 * every tier the parts' counts weigh, exactly. Keyed by the part most of its
 * units are, the first on a tie; null where a part has no entry.
 */
const blendedAllowance = (parts, grade) => {
  const entries = parts.map((p) => ({
    ...p,
    entry: PRODUCT_ALLOWANCES[p.key],
  }));
  if (!entries.length || entries.some((p) => !p.entry || !(p.count > 0)))
    return null;
  const total = entries.reduce((n, p) => n + p.count, 0);
  const tier = resolveAllowanceGrade(grade).tier;
  const weigh = (field) =>
    round2(entries.reduce((n, p) => n + p.entry[field] * p.count, 0) / total);
  const main = entries.reduce((a, b) => (b.count > a.count ? b : a));
  return {
    key: main.key,
    cost: weigh(tier),
    low: weigh('low'),
    high: weigh('high'),
    unit: main.entry.unit,
    source: entries.map((p) => p.entry.source).join(' '),
    derived: entries.some((p) => p.entry.derived),
    parts: entries.map(({ key, count }) => ({ key, count })),
  };
};

/**
 * The multiplier for a cabinet row's built-in seed at a grade, or 1 for
 * anything that is not a cabinet priced from our own seed.
 *
 * A catalog or Price Book price is never scaled: it is a real price for a
 * real product, and its grade is whatever the product is.
 *
 * The grade is the row's own `lineGrade` where someone chose a generic line
 * for the boxes (Generic Stock / Semi-Custom / Custom — a choice of grade, so
 * it outranks the job's), else the job's spec level, which is for what nobody
 * has chosen (user, 2026-10-15).
 */
const cabinetGradeFactor = (row, grade) => {
  if (!row || row.surfaceType !== CABINET_SURFACE) return 1;
  if (row.priceSource !== 'estimate') return 1;
  const key = row.lineGrade ?? resolveAllowanceGrade(grade).key;
  return CABINET_GRADE_MULTIPLIER[key] ?? 1;
};

module.exports = {
  TIERS,
  ALLOWANCE_TIERS,
  DEFAULT_ALLOWANCE_GRADE,
  resolveAllowanceGrade,
  gradeFromCabinetFamily,
  CABINET_GRADE_MULTIPLIER,
  allowanceKeyFor,
  allowanceFor,
  cabinetGradeFactor,
};
